/**
 * Le consegne partite, attraverso le commesse.
 *
 * **La ricevuta si apre sei mesi dopo** (§ 10.1), e fino a ieri si poteva
 * aprire solo entrando nella commessa che l'aveva prodotta — sapendo gia'
 * quale fosse. Qui la domanda e' quella vera: «che cosa e' partito, quando, e
 * chi l'ha firmato».
 *
 * La riga porta alla consegna della commessa e non a una seconda copia della
 * ricevuta: due schermate che mostrano la stessa cosa divergono, e quella
 * sbagliata la si scopre dal committente.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { EmptyState } from '../carta/EmptyState'
import { StatusPill, type PillTone } from '../carta/StatusPill'
import { day } from '../format'
import { useT } from '../i18n/LocaleProvider'
import { sent, type Delivered } from '../exports'

/**
 * Il tono dello stato.
 *
 * Il modello mostrava «Inviata» in viola: il viola e' `--st-lock` e il codice
 * di stile lo riserva a quello (§ 06), per cui qui prende il tono neutro —
 * una consegna in viaggio non e' ne' buona ne' cattiva notizia. Parola e
 * pallino restano, e sono loro a dire lo stato a chi non distingue i toni.
 */
type Translate = ReturnType<typeof useT>

function tone(state: string): PillTone {
  if (state === 'accepted') return 'ref'
  if (state === 'rejected') return 'sev-4'
  return 'ink-3'
}

function label(state: string, t: Translate): string {
  if (state === 'accepted') return t('deliveries.accepted')
  if (state === 'rejected') return t('deliveries.rejected')
  return t('deliveries.submitted')
}

export function Deliveries(): JSX.Element {
  const t = useT()
  const [rows, setRows] = useState<Delivered[] | null>(null)
  const [wrong, setWrong] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void sent()
      .then((found) => {
        if (!cancelled) setRows(found)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        setRows([])
        // Il messaggio mancante si traduce dipingendo, non qui: `useT()`
        // cambia identita' a ogni render, e una lettura che dipende da lei
        // si riarma da sola all'infinito.
        setWrong(error instanceof Error ? error.message : '')
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (rows === null) {
    return (
      <main className="consegne">
        <p className="tenue" role="status">
          {t('deliveries.loading')}
        </p>
      </main>
    )
  }

  if (wrong !== null) {
    return (
      <main className="consegne">
        <p role="alert">{wrong === '' ? t('deliveries.readFailed') : wrong}</p>
      </main>
    )
  }

  if (rows.length === 0) {
    // Il silenzio si legge come guasto: un elenco vuoto deve dire di essere
    // vuoto, e perche'.
    return (
      <main className="consegne">
        <EmptyState title={t('deliveries.emptyTitle')} action={t('deliveries.emptyAction')} />
      </main>
    )
  }

  return (
    <main className="consegne">
      <div className="tabella-larga">
        <table>
          <thead>
            <tr>
              <th>{t('deliveries.colSent')}</th>
              <th>{t('deliveries.colJob')}</th>
              <th className="numero">{t('deliveries.colDeclarations')}</th>
              <th>{t('deliveries.colState')}</th>
              <th>{t('deliveries.colSignedBy')}</th>
              <th>{t('deliveries.colChecksum')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((one) => (
              <tr key={one.export_id}>
                <td className="t-num">{day(one.sent_at)}</td>
                <td>
                  {/* La riga apre la ricevuta — e' cio' che qui si cerca — e
                      accanto la commessa (GI-279): prima portava solo alla
                      consegna della commessa, e la ricevuta stava due salti
                      piu' in la'. */}
                  <Link to={`/exports/${one.export_id}`} className="t-num">
                    {one.external_inspection_id}
                  </Link>{' '}
                  <Link to={`/inspections/${one.inspection_id}/delivery`} className="tenue">
                    {t('deliveries.openJob')}
                  </Link>
                </td>
                <td className="numero t-num">{one.declarations}</td>
                <td>
                  <StatusPill label={label(one.state, t)} tone={tone(one.state)} />
                  {/* Il motivo sta nella riga: «respinta» da sola costringe a
                      rovistare, e il motivo e' cio' per cui l'esito si registra. */}
                  {one.rejection_reason !== null && (
                    <span className="tenue"> · {one.rejection_reason}</span>
                  )}
                </td>
                <td>{one.sent_by ?? <span className="tenue">{t('deliveries.noAuthor')}</span>}</td>
                {/* L'impronta accorciata: intera non si legge, e serve a
                    riconoscere, non a verificare — chi verifica apre la ricevuta. */}
                <td className="t-num">{one.checksum.slice(0, 12)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </main>
  )
}
